import { Alert, AlertIcon, Box, Button, chakra, Collapse, FormControl, FormLabel, GridItem, IconButton, Img, Input, InputGroup, InputRightElement, SimpleGrid, Stack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiEye, HiEyeOff, HiUser } from "react-icons/hi";
import { ThreeDots } from "react-loader-spinner";
import { getDeviceInfo, toAbsoluteUrl } from "../../helpers";
import { useAuth } from "../../helpers/configs/plugins/CookieAuthPlugin";
import { APP_VERSION } from "../../helpers/environment";
import { login } from "../../services/apiClient/auth";

export default function Login() {
  const { t } = useTranslation();
  const { loginAuth, refetchUser } = useAuth();
  const { isOpen, onToggle } = useDisclosure();
  const [error, setError] = useState("");
  const recaptchaRef = useRef();
  const [logLoading, setLogLoading] = useState(false);

  // get device info
  const { deviceId, deviceName } = getDeviceInfo();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formProps = Object.fromEntries(formData);
    setLogLoading(true);
    let res = await login({ deviceId: deviceId, deviceName: deviceName, username: formProps.username, password: formProps.password });
    try {
      if (res?.header?.errorCode === "401" || res?.header?.statusCode === 401) {
        recaptchaRef.current.reset();
        setError(res?.header.errorText);
      }
      if (res.header.result) {
        await loginAuth({ token: res?.body?.accessToken });
        res && refetchUser();
      }
      if (!res.body.status) {
        recaptchaRef.current.reset();
        setError(res.body.message);
      }
    } catch (err) {
      setError(err.header?.errorCode === "401" ? t("label.something_went_wrong") : t("label.incorrect_username_or_password"));
    }
    setLogLoading(false);
  };

  return (
    <SimpleGrid pos="relative" columns="2" h="100vh" bg="white">
      <GridItem colSpan={{ base: 0, lg: 1 }} display={{ base: "none", lg: "block" }} bg="brand.500">
        <Box h="100vh" bgImage={toAbsoluteUrl("/assets/banner-login.webp")} bgSize="cover" bgPosition="center" />
      </GridItem>
      <GridItem colSpan={{ base: 2, lg: 1 }} fontFamily="inherit">
        <VStack w="full" h="full" bgRepeat="no-repeat" overflow="hidden" pb={{ base: "4rem", md: "6rem" }} justify="center" spacing="3rem">
          <VStack spacing="2">
            <Box h={{ base: "5rem", md: "8rem" }} bg="white" rounded="full">
              <Img h="full" src={toAbsoluteUrl("/assets/logo.png")} alt="" />
            </Box>
            <Text fontSize="4xl" fontWeight="semibold">
              {t("label.welcome")}
            </Text>
            <Text fontSize="lg">{t("label.please_login_to_your_account")}</Text>
          </VStack>
          <Box zIndex="banner" w={{ base: "90%", md: "70%" }}>
            <chakra.form onSubmit={handleSubmit}>
              <Stack spacing="4">
                <FormControl id="username">
                  <FormLabel fontWeight="medium" fontSize="20px">
                    {t("label.username")}
                  </FormLabel>
                  <InputGroup>
                    <InputRightElement h="full" mr="2">
                      <IconButton color="gray.500" _focus={{ outline: "none" }} bg="transparent !important" icon={<HiUser size="2em" />} />
                    </InputRightElement>
                    <Input bg="white" size="lg" py="25px" name="username" placeholder={t("label.enter_username")} autoComplete="text" required />
                  </InputGroup>
                </FormControl>
                <FormControl id="password">
                  <FormLabel fontWeight="medium" fontSize="20px">
                    {t("label.password")}
                  </FormLabel>
                  <InputGroup>
                    <InputRightElement h="full" mr="2">
                      <IconButton tabIndex="-1" bg="transparent !important" color="gray.500" variant="ghost" aria-label={isOpen ? "Mask password" : "Reveal password"} icon={isOpen ? <HiEye size="1.8em" /> : <HiEyeOff size="1.8em" />} onClick={onToggle} _focus={{ outline: "none" }} colorScheme="brand.100" />
                    </InputRightElement>
                    <Input bg="white" size="lg" py="25px" name="password" type={isOpen ? "text" : "password"} autoComplete="current-password" placeholder={t("label.enter_password")} required />
                  </InputGroup>
                </FormControl>

                {!logLoading && (
                  <Collapse in={error} animateOpacity>
                    <Alert status="error">
                      <AlertIcon />
                      {error}
                    </Alert>
                  </Collapse>
                )}

                <Button type="submit" textTransform="uppercase" letterSpacing="wide" py="25px" fontSize="lg" colorScheme="brand" isLoading={logLoading} spinner={<ThreeDots color="#fff" height={40} width={40} />} size="md">
                  {t("label.login")}
                </Button>
              </Stack>
            </chakra.form>
          </Box>
          <VStack opacity="0.9" spacing="0" pos="fixed" bottom="10" fontSize="md" fontWeight="bold">
            <Text>
              {t("label.powered_by")}
              <Text as="span" ml="2" color="brand.500">
                UDAYA TECHNOLOGY Co., Ltd.
              </Text>
            </Text>
            <Text>{t("label.version") + " " + APP_VERSION}</Text>
          </VStack>
        </VStack>
      </GridItem>
    </SimpleGrid>
  );
}
